/**
 * OsteopathyConsent Component
 *
 * Presentational component for the Osteopathy consent form.
 * Displays the consent text and signature canvas.
 *
 * Props:
 *   - checked: bool - Whether the osteopathy section is enabled
 *   - onChange: func(fieldName, value) - Callback for changes
 *   - answers: obj - Current values for osteopathy-related fields
 *   - signatureRef: ref - React ref for the signature canvas
 *   - onClearSignature: func - Callback to clear the signature
 *   - readOnly: bool (optional) - If true, disable interaction (for historical view)
 *   - version: obj (optional) - Version metadata for historical rendering
 */

import React from "react";
import {
  Typography,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import SignatureCanvas from "react-signature-canvas";

const OSTEOPATHY_VERSION_IDS = {
  V1: "1.0",
  V2: "2.0",
};

function OsteopathyConsentTextV1() {
  return (
    <Typography variant="body1" style={{ textAlign: "left" }}>
      Please carefully read this information, and if you have any questions,
      please ask our practitioner(s).
      <br /> <br />
      The information received and collected about our clients/patients from
      their visit(s) to our centre is private and confidential. It is only used
      and viewed by the healthcare professionals and staff employed or
      contracted by our centre, unless, in the best interest of the
      client/patient, a practitioner determines that there is a need to
      communicate with another person or healthcare professional outside of our
      centre (also, we will not give, share, sell, or transfer any personal
      information to a third party unless required by law). Our centre
      communicates with our clients by mail, email and or other electronic
      channels. On occasion, we may use client/patient information to conduct
      clinical studies to help us improve upon services provided. Our centre may
      allow interns and students to get access to clinical files to clinical
      files for observations and clinical placements purposes.
      <br /> <br />
      I understand that the Osteopath is providing a treatment services within
      their scope of practice.
      <br /> <br />
      I hereby give consent to my Therapist to treat me for the above noted
      purposes including such assessments, examinations and techniques, which
      may be recommended, by my Therapist.
      <br /> <br />
      I acknowledge that the Therapist is not a physician and does not diagnose
      illness or disease or any other physical or mental disorder. I clearly
      understand that an Osteopathy treatment is not a substitute for a medical
      examination. I acknowledge that no assurance or guarantee has been
      provided to me as to the results of the treatment. I acknowledge that with
      any treatment there can be risks and those risks have been explained to me
      and I assume those risks.
      <br /> <br />
      I acknowledge and understand that the Therapist must be fully aware of my
      existing medical conditions. I have completed my medical history form, as
      provided by my Therapist, and have disclosed to the Therapist all of those
      medical conditions affecting me. It is my responsibility to keep the
      Therapist updated on my medical history. The information I have provided
      is true and complete to the best of my knowledge.
      <br /> <br />
      I understand that patient files and subsequently information contained in
      files is shared among practitioners for the mutual benefit of patient
      treatment. I understand that written consent must be obtained for a
      practitioner to remove/transfer file information.
      <br /> <br />I understand that{" "}
      <b>
        any sexual comments, innuendos or contact will not be tolerated at any
        time
      </b>
      . The therapist reserves the right to terminate the treatment immediately
      and charge the full fee to violators.
      <br /> <br />I have read the above noted consent and have had the
      opportunity ask questions relating the contents of my therapist. By
      signing this form, I confirm my consent to treatment and intend this
      consent to cover the treatment discussed with me and such additional
      treatment as proposed by my Therapist from time to time, to deal with my
      physical condition and for which I have sought treatment. I understand
      that at any time I may withdraw my consent and treatment will be stopped.
    </Typography>
  );
}

function OsteopathyConsentTextV2({ answers, onChange, readOnly }) {
  return (
    <>
      <Typography variant="body1" style={{ textAlign: "left" }}>
        <b>
          Osteopathic Treatment Informed Consent, Treatment Authorization, and
          Clinic Policy Agreement
        </b>
        <br />
        <br />
        Please read this document carefully before treatment begins. If you have
        any questions regarding osteopathic treatment, assessment procedures,
        risks, billing, privacy, or clinic policies, please ask your
        practitioner before signing.
        <br />
        <br />
        <b>1. Nature of Osteopathic Treatment</b>
        <br />
        Osteopathic treatment involves manual assessment and treatment
        techniques intended to support mobility, function, posture,
        musculoskeletal balance, circulation, and overall physical well-being. I
        understand that osteopathic practitioners at Appleby Wellness Centre
        provide manual therapy services within their scope of practice and
        professional training. Treatment techniques may include, but are not
        limited to: Soft tissue therapy//Joint mobilization//Myofascial
        release//Trigger point therapy//Stretching// techniques//Cranial
        techniques//Visceral techniques//Postural assessment//Manual pressure
        techniques//Instrument-assisted soft tissue techniques//Use of
        therapeutic tools or devices composed of stone, metal, bone, silicone,
        plastic, wood, or similar materials//Use of fingers, thumbs, knuckles,
        forearms, elbows, or body-weight-assisted pressure. Treatment
        approaches, pressure, and techniques may vary depending on assessment
        findings, tissue response, patient presentation, safety considerations,
        and clinical judgment.
        <br />
        <br />
        <b>2. Practitioner Scope and Medical Acknowledgment</b>
        <br />
        I acknowledge and understand that the practitioner is not a medical
        doctor unless specifically identified otherwise. I understand that
        osteopathic treatment is not a substitute for: Medical
        diagnosis//Medical treatment//Emergency medical care//Diagnostic
        imaging//Specialist consultation. I understand that the practitioner
        does not diagnose medical diseases or conditions outside their
        professional scope. I understand that I may be advised to seek
        assessment or treatment from another healthcare provider where
        clinically appropriate. No guarantees or assurances have been made
        regarding treatment outcomes, recovery timelines, or symptom resolution.
        <br />
        <br />
        <b>3. Risks, Side Effects, and Treatment Response</b>
        <br />
        I understand that all manual therapy carries inherent risks and that
        treatment responses vary between individuals. Possible risks and side
        effects may include, but are not limited to: Temporary pain or
        discomfort//Post-treatment soreness or tenderness//Bruising//Swelling or
        inflammation//Temporary aggravation of symptoms//Muscle spasm or
        guarding//Fatigue//Dizziness or light-headedness//Headache//Tissue
        irritation or congestion//Skin sensitivity//Temporary reduction in
        mobility or function//Nerve irritation symptoms//Emotional or
        stress-related responses. I understand that some individuals may
        experience delayed onset soreness or significant discomfort following
        treatment, particularly following: Deep tissue work//Trigger point
        therapy//Pressure-based techniques//Mobilization
        techniques//Instrument-assisted soft tissue techniques. I understand
        that symptom aggravation may occur despite appropriate treatment being
        provided. I acknowledge that treatment outcomes depend on multiple
        factors, including: Existing medical conditions//Tissue
        sensitivity//Previous injuries//Physical condition//Daily
        activities//Exercise following treatment//Occupational strain//Hydration
        status//Compliance with recommendations//Individual healing response.
        <br />
        <br />
        <b>4. Patient Disclosure Responsibilities</b>
        <br />
        I understand that safe treatment depends on complete and accurate health
        information, and I agree to disclose all relevant information relating
        to: Medical conditions//Surgeries//Injuries//Imaging
        results//Medications//Pregnancy//Neurological symptoms//Cardiovascular
        conditions//Acute pain conditions//Allergies or sensitivities//Current
        healthcare providers involved in my care//Changes in my physical
        condition. I understand that failure to disclose relevant medical
        information may increase the risk of adverse responses to treatment. The
        information I have provided is true and complete to the best of my
        knowledge.
        <br />
        <br />
        <b>5. Consent to Assessment and Treatment</b>
        <br />
        I voluntarily consent to osteopathic assessment and treatment provided
        by the practitioner. I understand that treatment techniques may be
        modified during the appointment based on assessment findings, clinical
        judgment, tissue response, safety considerations, or patient tolerance.
        I understand that I may ask questions at any time//I may request changes
        in pressure or technique//I may refuse or discontinue any treatment
        technique//I may withdraw consent at any time. I understand that the
        practitioner may discontinue treatment where clinically indicated, where
        safety concerns arise, or where inappropriate conduct occurs.
        <br />
        <br />
        <b>6. Post-Treatment Care and Activity Acknowledgment</b>
        <br />
        I understand that temporary soreness, fatigue, tenderness, stiffness, or
        symptom aggravation may occur following osteopathic treatment. I
        acknowledge that I may be advised to: Rest following treatment//Avoid
        strenuous activity//Avoid heavy exercise or athletic training//Hydrate
        adequately//Apply heat or cold as directed//Monitor symptoms//Seek
        medical attention if symptoms worsen or persist. I understand that
        strenuous physical activity, gym training, repetitive work activity,
        sports participation, aqua training, or other physically demanding
        activities immediately following treatment may contribute to increased
        soreness, irritation, inflammation, or aggravation of symptoms. I
        acknowledge that post-treatment recommendations are provided for safety
        and recovery purposes and that failure to follow recommendations may
        affect treatment response.
        <br />
        <br />
        <b>7. Privacy, Documentation, and Communication</b>
        <br />
        All personal health information collected by Appleby Wellness Centre is
        handled in accordance with applicable privacy legislation and
        professional standards. Information may be collected, used, stored, and
        disclosed for purposes including: Assessment and treatment//Clinical
        documentation//Billing and insurance claims//Risk management//Quality
        assurance//Communication with authorized healthcare
        providers//Regulatory compliance//Legal proceedings where required by
        law//Insurer audits or investigations. I understand that patient files
        may be accessed by authorized staff, practitioners, students, interns,
        auditors, insurers, legal representatives, or regulatory bodies where
        permitted or required by law. Electronic communication, including email
        and electronic billing, carries inherent privacy and cybersecurity
        risks. By communicating electronically with the clinic, I acknowledge
        and accept these risks.
        <br />
        <br />
        <b>8. Direct Billing and Insurance Acknowledgment</b>
        <br />
        I understand that direct billing is offered as a convenience only and is
        not guaranteed. I acknowledge that: I remain financially responsible for
        all services rendered//Insurance approval does not guarantee
        payment//Insurers may audit, investigate, deny, reverse, or recover
        claims//The clinic may communicate directly with insurers regarding
        submitted claims//Overpayment recovery processes must occur directly
        between the clinic and the insurer//Submission of inaccurate or
        fraudulent claims may result in repayment obligations, insurer
        investigation, regulatory reporting, or legal consequences. I authorize
        the clinic to communicate with insurers, benefits administrators,
        auditors, and authorized representatives for purposes relating to claims
        administration, verification, audits, investigations, and overpayment
        recovery.
        <br />
        <br />
        <b>9. Professional Conduct and Safety</b>
        <br />
        I understand that any inappropriate behaviour, including sexual
        comments, sexual advances, harassment, threatening conduct,
        intoxication, or inappropriate physical contact, will result in
        immediate termination of treatment. The practitioner and clinic reserve
        the right to refuse or discontinue treatment where safety, professional
        boundaries, or clinic policies are compromised.
        <br />
        <br />
        <b>10. Consent Confirmation</b>
        <br />
        By signing below, I confirm that:
        <br />
        <ul style={{ marginTop: 8, marginBottom: 8 }}>
          <li>I have read and understood this document in full</li>
          <li>I have had the opportunity to ask questions</li>
          <li>My questions have been answered to my satisfaction</li>
          <li>
            I understand the nature and limitations of osteopathic treatment
          </li>
          <li>I understand the potential risks and side effects</li>
          <li>I voluntarily consent to assessment and treatment</li>
          <li>
            I understand that no specific outcome or recovery timeline has been
            guaranteed
          </li>
          <li>I understand that I may withdraw consent at any time</li>
          <li>
            I authorize collection, use, and disclosure of information as
            outlined above
          </li>
          <li>
            I understand and accept clinic policies relating to treatment,
            billing, documentation, and insurer communication
          </li>
        </ul>
        <br />
        <b>Optional Acknowledgments</b>
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please review and acknowledge each statement below:
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers?.osteo_temporarySoreness || false}
            onChange={(e) =>
              onChange("osteo_temporarySoreness", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that temporary soreness or symptom aggravation may occur following treatment."
      />{" "}
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers?.osteo_pressure || false}
            onChange={(e) => onChange("osteo_pressure", e.target.checked)}
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that pressure-based and deep tissue techniques may cause tenderness or bruising."
      />{" "}
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers?.osteo_strenuousActivity || false}
            onChange={(e) =>
              onChange("osteo_strenuousActivity", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that strenuous physical activity immediately following treatment may increase soreness or irritation."
      />{" "}
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers?.osteo_communicateDiscomfort || false}
            onChange={(e) =>
              onChange("osteo_communicateDiscomfort", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that I should communicate discomfort during treatment so techniques or pressure may be modified."
      />{" "}
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers?.osteo_noSpecificOutcome || false}
            onChange={(e) =>
              onChange("osteo_noSpecificOutcome", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that no specific treatment outcome or recovery timeline can be guaranteed."
      />
    </>
  );
}

function OsteopathyConsent({
  checked,
  onChange,
  answers,
  signatureRef,
  onClearSignature,
  readOnly,
  version,
}) {
  if (!checked) {
    return null;
  }

  const activeVersionId = version?.versionId || OSTEOPATHY_VERSION_IDS.V2;

  return (
    <div>
      {/* Historical disclaimer if viewing old version */}
      {readOnly && version && (
        <Card style={{ marginBottom: 16, backgroundColor: "#fffacd" }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#856404" }}>
              <b>Historical View:</b> This form was submitted on{" "}
              {version.submittedAt?.split("T")[0]} under consent version{" "}
              {version.versionId} and is displayed for reference only.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Typography variant="h4" style={{ textAlign: "left" }}>
        Osteopathy Consent Form
      </Typography>
      <br />
      <br />
      {activeVersionId === OSTEOPATHY_VERSION_IDS.V1 ? (
        <OsteopathyConsentTextV1 />
      ) : (
        <OsteopathyConsentTextV2
          answers={answers}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please sign in the box below.
      </Typography>
      <div
        style={{
          borderStyle: "dotted",
          borderWidth: 2,
          borderColor: "#D3D3D3",
          width: 530,
          height: 240,
          borderRadius: 4,
        }}
      >
        <SignatureCanvas
          penColor="black"
          ref={signatureRef}
          canvasProps={{
            width: 530,
            height: 240,
            className: "consentOsteoSig",
          }}
          disabled={readOnly}
        />
      </div>
      <br />
      {!readOnly && <Button onClick={onClearSignature}>Clear</Button>}
      <br />
      <br />
    </div>
  );
}

export default OsteopathyConsent;
